import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { POSTING_DOCS, type PendingDocDto, type PostingBatchDto, type PostingDoc, type PostingMode, type PostingPoliciesDto, type PostingRight } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText } from "../../lib/errors";
import { useSession } from "../../lib/session";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";
import { amt } from "../ledger/common";
import { DemoLockNotice } from "../../components/Demo";

// Settings › Posting (migration 034, rules PP-1 to PP-6): the posting policy per document type with what is waiting
// for each, the documents waiting for posting (posted by a poster here, several at once), and who else may post. The
// server decides every change and every posting; locked controls show the reason.

const docPath = (d: PendingDocDto) =>
  d.family === "invoice" ? `/invoices/show/${d.id}` : d.family === "journal" ? `/journals/show/${d.id}`
    : d.docType === "cash_transfer" ? `/transfer/${d.id}` : d.docType === "opening_balance" ? `/opening-balance/${d.id}` : `/receipt/${d.id}`;

export function PostingSettings() {
  const { t, lang } = useLang();
  const s = useSession();
  const toast = useToast();
  const [x, setX] = useState<PostingPoliciesDto | null>(null);
  const [pending, setPending] = useState<PendingDocDto[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [outcome, setOutcome] = useState<PostingBatchDto | null>(null);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const [p, w] = await Promise.all([api<PostingPoliciesDto>("GET", "/posting/policies"), api<PendingDocDto[]>("GET", "/posting/pending")]);
      setX(p); setPending(w); setPicked(new Set());
    } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);
  const when = (iso: string) => new Date(iso).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Riyadh" });
  const docName = (d: PostingDoc) => t(`doc_${d}`);
  const mayPost = (d: PostingDoc) => !!s.me?.posting.post.includes(d);

  async function setMode(d: PostingDoc, mode: PostingMode) {
    setBusy(d);
    try {
      setX(await api<PostingPoliciesDto>("PUT", `/posting/policies/${d}`, { mode }));
      toast(t("psSaved", { d: docName(d), m: t(mode === "automatic" ? "psAutomatic" : "psManual") }));
      await s.refresh();
    } catch (e) { toast(errorText(t, e)); } finally { setBusy(null); }
  }
  async function setRight(userId: string, name: string, rights: PostingRight[]) {
    setBusy(userId);
    try { setX(await api<PostingPoliciesDto>("PUT", `/posting/rights/${userId}`, { rights })); toast(t("psRightsSaved", { u: name })); }
    catch (e) { toast(errorText(t, e)); } finally { setBusy(null); }
  }
  async function postSelected() {
    setBusy("batch");
    try {
      const items = (pending ?? []).filter((p) => picked.has(p.id)).map((p) => ({ family: p.family, id: p.id }));
      const r = await api<PostingBatchDto>("POST", "/posting/batch", { items });
      setOutcome(r);
      toast(t("psPosted", { n: r.posted }));
      await load();
    } catch (e) { toast(errorText(t, e)); } finally { setBusy(null); }
  }

  const locked = x && !x.canManage ? <p className="hint" data-testid="ps-locked"><Icon name="lock" /> {t(x.ownerAssigned ? "psOwnerOnly" : "psNoOwner")}</p> : null;
  const body = failed ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div></section>
  ) : !x || !pending ? <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section> : (
    <div className="set-stack">
      <div className="dep-note"><Icon name="info" /><span>{t("psIntro")}</span></div>
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("setPosting")}</h2></div>
        <div className="card set-sec-b">
          {locked}
          <div className="table-wrap"><table className="tbl" data-testid="ps-policies">
            <thead><tr><th>{t("psDoc")}</th><th>{t("psMode")}</th><th className="end">{t("psWaiting")}</th><th className="end">{t("psWaitingTotal")}</th><th>{t("psChanged")}</th></tr></thead>
            <tbody>{x.policies.map((p) => (
              <tr key={p.docType} data-doc={p.docType}>
                <td>{docName(p.docType)}</td>
                <td><select className="control" style={{ minWidth: 120 }} aria-label={`${t("psMode")}: ${docName(p.docType)}`} value={p.mode} disabled={!x.canManage || busy === p.docType}
                  onChange={(e) => void setMode(p.docType, e.target.value as PostingMode)} data-testid={`ps-mode-${p.docType}`}>
                  <option value="automatic">{t("psAutomatic")}</option><option value="manual">{t("psManual")}</option>
                </select></td>
                <td className="end num" data-testid={`ps-pending-${p.docType}`}>{p.pending.count}</td>
                <td className="end num">{p.pending.count ? amt(p.pending.total) : "–"}</td>
                <td className="muted">{p.isDefault ? t("psDefault") : `${p.updatedByName ?? "–"} · ${p.updatedAt ? when(p.updatedAt) : ""}`}</td>
              </tr>))}</tbody>
          </table></div>
        </div>
      </section>

      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("psPendingTitle")}</h2>
          <button className="btn btn-primary btn-sm" disabled={!picked.size || busy === "batch"} onClick={() => void postSelected()} data-testid="ps-post-selected"><Icon name="ledger" />{t("psPostSelected")}</button></div>
        <div className="card set-sec-b">
          {outcome && outcome.results.some((r) => !r.ok) && <div className="dep-note warn" data-testid="ps-outcome"><Icon name="alert" /><span>{t("psNotPosted", { n: outcome.results.filter((r) => !r.ok).length })}: {outcome.results.filter((r) => !r.ok).map((r) => r.message).join(" · ")}</span></div>}
          {!pending.length ? <p className="muted" data-testid="ps-pending-none">{t("psPendingNone")}</p> : (
            <div className="table-wrap"><table className="tbl" data-testid="ps-pending">
              <thead><tr><th /><th>{t("psDoc")}</th><th>{t("date")}</th><th>{t("statement")}</th><th className="end">{t("total")}</th><th>{t("psBy")}</th><th>{t("psSince")}</th></tr></thead>
              <tbody>{pending.map((d) => (
                <tr key={d.id}>
                  <td><input type="checkbox" aria-label={d.code ?? docName(d.docType)} disabled={!mayPost(d.docType)} title={mayPost(d.docType) ? undefined : t("errOwnerOnly")}
                    checked={picked.has(d.id)} onChange={(e) => setPicked((o) => { const n = new Set(o); if (e.target.checked) n.add(d.id); else n.delete(d.id); return n; })} /></td>
                  <td><Link to={docPath(d)}>{d.code ?? docName(d.docType)}</Link>{d.code && <span className="muted"> {docName(d.docType)}</span>}</td>
                  <td className="num">{d.at.replace("T", " ")}</td>
                  <td>{[d.party, d.statement].filter(Boolean).join(" · ")}</td>
                  <td className="end num">{amt(d.total)}</td>
                  <td>{d.byName ?? "–"}</td>
                  <td className="muted">{when(d.waitingSince)}</td>
                </tr>))}</tbody>
            </table></div>)}
        </div>
      </section>

      {x.canManage && <section className="set-sec">
        <div className="set-sec-h"><h2>{t("psRightsTitle")}</h2></div>
        <div className="card set-sec-b">
          <p className="hint">{t("psRightsIntro")}</p>
          <div className="table-wrap"><table className="tbl dense" data-testid="ps-rights">
            <thead><tr><th>{t("psUser")}</th>{POSTING_DOCS.map((d) => <th key={d} className="end" style={{ fontSize: 11 }}>{docName(d)}</th>)}<th className="end">{t("psManage")}</th></tr></thead>
            <tbody>{x.users.filter((u) => u.active).map((u) => {
              const has = new Set(u.rights);
              const toggle = (r: PostingRight, on: boolean) => void setRight(u.id, u.name, [...u.rights.filter((z) => z !== r), ...(on ? [r] : [])]);
              return <tr key={u.id} data-user={u.name}>
                <td>{u.name}{u.owner && <span className="pill good" style={{ marginInlineStart: 6 }}>{t("psOwnerAll")}</span>}</td>
                {[...POSTING_DOCS, "manage" as const].map((r) => <td key={r} className="end">
                  <input type="checkbox" aria-label={`${u.name}: ${r === "manage" ? t("psManage") : docName(r)}`} checked={u.owner || has.has(r)}
                    disabled={u.owner || busy === u.id || (r === "manage" && !x.owner) || u.id === s.me?.user.id} onChange={(e) => toggle(r, e.target.checked)} data-testid={`ps-right-${r}`} />
                </td>)}
              </tr>;
            })}</tbody>
          </table></div>
        </div>
      </section>}
    </div>
  );

  return (
    <Shell navKey="/settings/posting">
      <DemoLockNotice />
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setPosting")]]} title={t("setPosting")} />
      {body}
    </Shell>
  );
}
